import { historicalValues } from '@/apollo/queries/reports/historicalValues.gql'
import { recentStats } from '@/apollo/queries/reports/recentStats.gql'
import { reportBase } from '@/apollo/queries/reports/reportBase.gql'
import { defineComponent } from 'vue'
import { GraphqlQueryResponse } from '~/types/apolloTypes'
import { Dataset, HistoricalValues } from '~/types/reportTypes'
import {
  ComplianceIssue,
  ComplianceIssueOccurrence,
  RecentStat,
  Report,
  ReportLevel,
  Repository
} from '~/types/types'
import { DateRangeOptionT, dateRangeOptions, getDateRange } from '~/utils/date'
import { roundToSignificantNumber } from '~/utils/number'
import { prepareLabels } from '~/utils/reports'

/**
 * Mixin for reports queries and utilities
 */
export default defineComponent({
  name: 'ReportMixin',
  data() {
    return {
      report: {} as Report,
      recentStats: [] as Array<RecentStat>,
      historicalValues: { labels: [], values: {} } as HistoricalValues,
      labels: [] as Array<string>,
      datasets: [] as Array<Dataset>,
      complianceIssueList: [] as Array<ComplianceIssue>,
      complianceIssuesSeverityMap: {} as ComplianceIssueOccurrence,
      reportsDataLoading: false,
      historicalValuesLoading: false,
      recentStatsLoading: false,
      distributionStatsLoading: false,
      complianceIssuesLoading: false,
      dateRangeOptions: dateRangeOptions as Record<string, DateRangeOptionT>,
      dateRangeFilter:
        this.$cookies.get('reports-default-daterange-filter') ?? Object.keys(dateRangeOptions)[0]
    }
  },
  computed: {
    maxDigitHistoricValues(): number {
      if (this.historicalValues?.values?.count) {
        return Math.max(...this.historicalValues.values.count)
      }
      return 0
    },
    maxLineClip(): number {
      const exaggeratedMax = this.maxDigitHistoricValues * 1.25
      return roundToSignificantNumber(exaggeratedMax, exaggeratedMax.toString().length - 1)
    },
    maxBarClip(): number {
      return roundToSignificantNumber(
        this.maxDigitHistoricValues,
        this.maxDigitHistoricValues.toString().length - 1
      )
    },
    shouldChartBeShown(): boolean {
      return !(this.historicalValuesLoading || this.labels.length < 2 || this.datasets.length === 0)
    },
    currentVal(): number {
      return this.report?.currentValue ?? 0
    },
    reportRerenderKey(): string {
      return `${this.dateRangeFilter}-${this.maxDigitHistoricValues}-${this.datasets.length}`
    }
  },
  methods: {
    /**
     * Check whether repo details exist and whether they are of the active repo.
     *
     * @param {Repository} repository
     * @returns boolean
     */
    validateRepoDetails(repository: Repository): boolean {
      const { repo, provider, owner } = this.$route.params

      // if repo id absent, repo details don't exist
      if (!repository.id) {
        return false
      }

      // next, validate if repo details are of current active repo

      // if provider in route is different from repo provider, repo details are of
      // a different repo
      if (this.$providerMetaMap[provider].value !== repository.vcsProvider) {
        return false
      }

      // check if repo full name is correct
      if (repository.fullName !== `${owner}/${repo}`) {
        return false
      }

      return true
    },

    /**
     * Generate route to issue page with applied tags
     *
     * @param tag string
     * @returns string
     */
    issueRoute(tag: string): string {
      const route = this.$generateRoute(['issues'])
      return `${route}?category=all&analyzer=all&q=tag:${tag}`
    },

    /**
     * Fetch basic data of report.
     *
     * @param {ReportLevel} level
     * @param {string} objectId
     * @param {string} key
     */
    async fetchReportBase(level: ReportLevel, objectId: string, key: string, refetch = false) {
      if (objectId && level && key) {
        this.reportsDataLoading = true

        try {
          const response = (await this.$fetchGraphqlData(
            reportBase,
            {
              level,
              objectId,
              key
            },
            refetch
          )) as GraphqlQueryResponse
          this.report = response.data.report as Report
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch report details, please contact support.'
          )
        } finally {
          this.reportsDataLoading = false
        }
      }
    },

    /**
     * Fetch and set recent stats of a report.
     *
     * @param {ReportLevel} level
     * @param {string} objectId
     * @param {string} key
     */
    async fetchRecentStats(level: ReportLevel, objectId: string, key: string, refetch = false) {
      if (objectId && level && key) {
        this.recentStatsLoading = true

        try {
          const response = (await this.$fetchGraphqlData(
            recentStats,
            {
              level,
              objectId,
              key
            },
            refetch
          )) as GraphqlQueryResponse
          this.recentStats = response.data.report?.recentStats as Array<RecentStat>
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch recent stats of report, please contact support.'
          )
        } finally {
          this.recentStatsLoading = false
        }
      }
    },

    /**
     * Fetch historical values of a report and trigger label preparation.
     *
     * @param {ReportLevel} level
     * @param {string} objectId
     * @param {string} key
     */
    async fetchHistoricalValues(
      level: ReportLevel,
      objectId: string,
      key: string,
      refetch = false
    ) {
      const defaultDateRangeFilter = this.$cookies.get('reports-default-daterange-filter')

      if (this.dateRangeFilter !== defaultDateRangeFilter) {
        this.$cookies.set('reports-default-daterange-filter', this.dateRangeFilter)
      }

      const { startDate, endDate } = getDateRange(this.dateRangeFilter)

      if (objectId && level && key) {
        /**
         * More often than not, we're hitting Apollo cache but historicalValuesLoading
         * still changes everytime, causing flixker of empty-chart.
         * We're adding a manual setTimeout to check if we're hitting cache or not
         * and setting historicalValues accordingly.
         */
        const setTrueTimeout = setTimeout(() => {
          this.historicalValuesLoading = true
        }, 20)
        try {
          const response = (await this.$fetchGraphqlData(
            historicalValues,
            {
              level,
              objectId,
              key,
              startDate,
              endDate
            },
            refetch
          )) as GraphqlQueryResponse
          clearTimeout(setTrueTimeout)
          this.historicalValues = (await response.data.report?.historicalValues) as HistoricalValues

          // Only setting labels in mixin cause they are common accross every page.
          // Datasets are not.
          this.labels = prepareLabels(this.historicalValues.labels, startDate, endDate)
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch historical data of report, please contact support.'
          )
        } finally {
          this.historicalValuesLoading = false
        }
      }
    }
  }
})
